import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { useApiHeaders } from "@/hooks/use-api-headers";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type FoodCategory = {
  id: string;
  name: string;
  parent_id: string | null;
  sort_order: number;
};
export type InventoryItem = {
  id: string;
  description: string;
  simple_name: string | null;
  clean_name: string | null;
};

// ── Shared data sources (relabel picker + manual log) ─────────────────────

export function useFoodCategories(enabled: boolean) {
  const headers = useApiHeaders();
  return useQuery<FoodCategory[]>({
    queryKey: ["food-categories"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/food-categories`, { headers });
      if (!res.ok) throw new Error(`Failed to load categories: ${res.status}`);
      return (await res.json()).data;
    },
    enabled,
  });
}

export function useSiteInventoryListId(siteId: string | null, enabled: boolean) {
  const headers = useApiHeaders();
  return useQuery<string>({
    queryKey: ["site-inventory-list", siteId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites/${siteId}/inventory`, { headers });
      if (!res.ok) throw new Error(`Failed to resolve inventory: ${res.status}`);
      return (await res.json()).data.list.id as string;
    },
    enabled: enabled && !!siteId,
  });
}

export function useInventoryItems(listId: string | null, subcategoryId: string | null) {
  const headers = useApiHeaders();
  return useQuery<InventoryItem[]>({
    queryKey: ["inventory-items", listId, subcategoryId],
    queryFn: async () => {
      const params = new URLSearchParams({ subcategory_id: subcategoryId!, limit: "200" });
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items?${params}`, {
        headers,
      });
      if (!res.ok) throw new Error(`Failed to load items: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!listId && !!subcategoryId,
  });
}

// ── Shared presentational primitives ──────────────────────────────────────

export function OptionList<T extends { id: string }>({
  options,
  empty,
  showChevron,
  disabled,
  onSelect,
  render,
}: {
  options: T[];
  empty: string;
  showChevron: boolean;
  disabled?: boolean;
  onSelect: (option: T) => void;
  render: (option: T) => string;
}) {
  if (options.length === 0) {
    return <p className="px-2 py-6 text-center text-sm text-gray-400">{empty}</p>;
  }
  return (
    <ul className="flex flex-col">
      {options.map((option) => (
        <li key={option.id}>
          <button
            type="button"
            onClick={() => onSelect(option)}
            disabled={disabled}
            className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            <span className="truncate">{render(option)}</span>
            {showChevron && <ChevronRight className="size-4 shrink-0 text-gray-300" aria-hidden />}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ListSkeleton() {
  return (
    <div className="flex flex-col gap-1 py-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}
